import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import { COLORS } from "../../assets/theme";
import { countries } from "../../assets/utils/countries";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomLabel from "../../components/CustomLabel";

const CLientsInfo: React.FC = () => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [importOpen, setImportOpen] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);

  const [search, setSearch] = React.useState<string>("");

  const formFields: Field[] = [
    { name: "fullName", label: "Name" },
    { name: "description", label: "Description" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState(() => {
    const total = 10;
    return Array.from({ length: total }, (_, i) => {
      const id = i + 1;
      const name = `Client ${id}`;
      const description = `Description for Client ${id}`;

      return {
        id,
        name,
        description,
      };
    });
  });

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="CLientsInfo"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          fields={[
            { key: "id", header: "ID" },
            { key: "name", header: "Name" },
            { key: "description", header: "Description" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [r.name, r.description, r.id]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit CLientsInfo` : "Add CLientsInfo"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? Object.keys(editingRow).reduce((acc: any, key) => {
                  const val = editingRow[key];
                  acc[key] =
                    val === undefined || val === null ? "" : String(val);
                  return acc;
                }, {})
              : {}
          }
          onSubmit={(values) => {
            if (editingRow) {
              setRows((prev) =>
                prev.map((r: any) =>
                  String(r.id) === String(editingRow.id)
                    ? { ...r, ...values }
                    : r
                )
              );
            } else {
              setRows((prev) => {
                const nextId = prev.length
                  ? Math.max(...prev.map((p) => p.id)) + 1
                  : 1;
                const newRow = { id: nextId, ...values } as any;
                return [newRow, ...prev];
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={() => {
            if (toDeleteId === null) return;
            setRows((prev) =>
              prev.filter((r: any) => String(r.id) !== String(toDeleteId))
            );
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
        />
      </Box>
    </Box>
  );
};

export default CLientsInfo;
